<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="col-sm-12">
      <div class="card card-body">
        <div class="overflow-auto">
          <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
            <thead>
            <tr>
              <td></td>
              <th v-if="isAdmin" class="text-capitalize-first">{{ helper.trans('labels.id') }}</th>
              <th class="text-capitalize-first">{{ helper.trans('labels.name') }}</th>
              <th v-if="isAdmin" class="text-capitalize-first">{{ helper.trans('labels.merchant') }}</th>
              <th class="text-end">
                <button v-if="isAdmin || isMerchant" type="button" v-on:click="showEditModal({})" class="btn btn-outline-primary btn-sm text-capitalize">{{ helper.trans('button.create') }}</button>
                <b-modal dialog-class="modal-lg"
                         :title="createModalTitle"
                         :modelValue="createModal"
                         body-class="pt-0"
                         @update:modelValue="createModal = $event"
                >
                  <template #default>
                    <MerchantOperatorForm ref="createForm" :merchantFixed="merchantFixed"></MerchantOperatorForm>
                  </template>
                  <template #footer>
                    <button type="button" v-on:click="createModal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">{{ helper.trans('button.cancel') }}</button>
                    <LoaderWrapper ref="loaderCreateFrom" :zoom="0.5">
                      <button type="button" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans('button.save') }}</button>
                    </LoaderWrapper>
                  </template>
                </b-modal>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in response.items" :key="user.id" class="">
              <td>
                <i v-b-tooltip.hover :title="helper.trans('labels.user_blocked')" v-if="user.isBlocked" class="fa fa-ban text-danger"></i>
              </td>
              <td v-if="isAdmin">{{ user.id }}</td>
              <td>
                <UserNameLabel :user="user"></UserNameLabel>
              </td>
              <td v-if="isAdmin">
                <UserNameLabel v-if="user._merchant" :user="user._merchant"></UserNameLabel>
              </td>
              <td class="text-end">
                <button v-if="isAdmin && store.getters.hasPermission(UserPermission.VIEW_USER_SESSION_HISTORY)" type="button" @click.prevent="downloadSessionHistory(user.id)" class="btn btn-sm btn-outline-secondary text-nowrap text-capitalize me-2">
                  <i class="fa fa-file-download"></i> {{ helper.trans('button.download_session_history') }}
                </button>
                <button v-if="isAdmin" type="button" @click="showEditModal(user)" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa fa-pencil"></i> {{helper.trans('button.edit')}}</button>
                <button v-if="isOperator && store.getters.hasPermission(UserPermission.RESET_MERCHANT_PASSWORD)" type="button" @click.prevent="showChangePasswordModal(user)" class="btn btn-sm btn-outline-secondary text-nowrap text-capitalize">
                  <i class="fa fa-key"></i> {{helper.trans('button.change_password')}}
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <b-pagination
            v-if="response.totalCount > response.items.length"
            v-model="page"
            :total-rows="response.totalCount"
            :per-page="response.limit"
            :limit="20"
            @update:modelValue="loadList()"
            prev-text="Back"
            next-text="Next"
            align="start"
            first-number
            hide-goto-end-buttons
            hide-ellipsis
          ></b-pagination>

        </div>
      </div>
    </div>

    <InviteLinkConfirmModal ref="inviteLinkConfirmModal"></InviteLinkConfirmModal>
    <ChangePasswordModal ref="changePasswordModal"></ChangePasswordModal>
  </LoaderWrapper>
</template>

<script lang="ts">

import {computed, defineComponent, onMounted, ref} from 'vue'
import {helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import APIClient from "@/service/APIClient";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {useSessionHistoryDownload} from "@/service/useSessionHistoryDownload";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/components/CopyButton.vue";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import InviteLinkConfirmModal from "@/components/InviteLinkConfirmModal.vue";
import {UserRole} from "@/models/enum/UserRole";
import AccountWidget from "@/components/AccountWidget.vue";
import UserNameLabel from "@/components/UserNameLabel.vue";
import {useStore} from "vuex";
import AccountOperationsModal from "@/components/AccountOperationsModal.vue";
import MerchantOperatorDTO from "@/models/MerchantOperatorDTO";
import PaginationDTO from "@/models/PaginationDTO";
import MerchantDTO from "@/models/MerchantDTO";
import {Dictionary} from "@/types";
import MerchantOperatorFormDTO from "@/models/form/MerchantOperatorFormDTO";
import MerchantOperatorForm from "@/components/Form/MerchantOperatorForm.vue";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import {UserPermission} from "@/models/enum/UserPermission";

const api = new APIClient();

export default defineComponent({
  components: {
    MerchantOperatorForm,
    UserNameLabel,
    AccountWidget,
    LoaderWrapper,
    CopyButton,
    InviteLinkConfirmModal,
    AccountOperationsModal,
    ChangePasswordModal
  },
  setup(props) {
    const store = useStore()
    const merchantFixed = computed(() => store.getters.isMerchant ? store.getters.getUser : null)
    const defaultCreateForm = {
      id: null,
      name: null,
      email: null,
      password: '',
      isBlocked: false,
      createInvite: true,
      appId: null,
      merchant: null,
      isManualWithdrawals: false,
      permissions: [],
      is2faEnabled: null,
      role: UserRole.MERCHANT_OPERATOR
    } as MerchantOperatorFormDTO

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const loaderCreateFrom = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const createForm = ref<InstanceType<typeof MerchantOperatorForm> | null>(null)
    const inviteLinkConfirmModal = ref<InstanceType<typeof InviteLinkConfirmModal> | null>(null)
    const changePasswordModal = ref<InstanceType<typeof ChangePasswordModal> | null>(null)

    const state = reactive({
      createModalTitle: null,
      createModal: false,
      page: 1,
      response: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<MerchantOperatorDTO>
    });

    const loadList = async () => {
      loader.value?.show()
      state.response.offset = state.response.limit * (state.page - 1)
      state.response = await api.merchantOperators(state.response, {});
      if (store.getters.isMerchant) {
        for (const item of state.response.items) {
          item._merchant = merchantFixed.value ?? null
        }
      } else {
        let merchantIds = state.response.items.map(operator => operator.merchantId)
        let merchants = {} as Dictionary<MerchantDTO>
        for (const merchant of (await api.merchants({offset:0, limit: merchantIds.length} as PaginationDTO, {ids: merchantIds})).items) {
          merchants[merchant.id] = merchant
        }

        for (const item of state.response.items) {
          item._merchant = merchants[item.merchantId] ?? null
        }
      }

      loader.value?.hide()
    }

    const showEditModal = async (dto: MerchantOperatorDTO) => {
      if (dto.id) {
        state.createModalTitle = helper.trans("labels.edit_user", {name: dto.name})
        createForm.value?.setForm({
          ...defaultCreateForm,
          ...dto,
          merchant: dto._merchant
        } as MerchantOperatorFormDTO)
      } else {
        state.createModalTitle = helper.trans("labels.create_user")
        createForm.value?.setForm({...defaultCreateForm, password: helper.generatePassword()} as MerchantOperatorFormDTO)
      }

      state.createModal = true;
    }

    const showChangePasswordModal = (user: MerchantOperatorDTO) => {
      changePasswordModal.value?.show(user.id, api.merchantOperatorChangePassword)
    }

    const save = () => {
      loaderCreateFrom.value?.show()

      createForm.value!.save()
        .then((user: MerchantOperatorDTO) => {
          state.createModal = false;
          if (user.inviteToken) {
            const url = store.getters['getInviteLink'](user);
            inviteLinkConfirmModal.value?.show(url);
          }
          loadList();
        })
        .finally(() => {
          loaderCreateFrom.value?.hide();
        })

    }

    onMounted(() => {
      loadList()
    });

    const {downloadSessionHistory} = useSessionHistoryDownload(loader)

    return {
      downloadSessionHistory,
      ...toRefs(state),
      loader,
      createForm,
      loaderCreateFrom,
      dateHelper,
      numberFormatter,
      helper,
      showEditModal,
      save,
      loadList,
      inviteLinkConfirmModal,
      UserRole,
      store,
      isOperator: computed(() => store.getters.isOperator),
      isAdmin: computed(() => store.getters.isAdmin),
      isMerchant: computed(() => store.getters.isMerchant),
      merchantFixed,
      changePasswordModal,
      showChangePasswordModal,
      UserPermission,
    }
  }
})
</script>
